import { Segmented } from '../../ui/bits';
import { Icon } from '../../ui/Icon';
import { peso } from '../../lib/format.js';
import { cardEstimatePreview, newKey, setDefaultBand, type ProductDraft } from '../lib/draft.js';
import { gramsLabel } from '../lib/list.js';
import { Field, ListError, PesoInput, invalid, type Errors, type SetField, type Update } from './fields';

/** Fresh: unit or weight; weight → ₱/kg, stock unit, size bands or a target range; prep options. */
export function FreshSection({ d, errors, set, update, locked }: { d: ProductDraft; errors: Errors; set: SetField; update: Update; locked: boolean }) {
  const est = cardEstimatePreview(d);
  return (
    <section className="card panel" aria-labelledby="sec-fresh">
      <h2 id="sec-fresh">Fresh</h2>
      {d.pricing === 'weight' ? (
        <>
          <div className="cat-grid2">
            <Field name="pricePerKg" label="Price per kg" error={errors.pricePerKg} help="Customers pay for the actual weight at packing.">
              <PesoInput name="pricePerKg" value={d.pricePerKg} error={errors.pricePerKg} suffix="/kg" onChange={(v) => set('pricePerKg', v)} />
            </Field>
            <div className="field">
              <span className="flabel" id="lbl-stockunit">
                Count stock in
              </span>
              <Segmented<'piece' | 'gram'>
                label="Count stock in"
                value={d.stockUnit}
                options={[
                  { value: 'piece', label: 'Pieces' },
                  { value: 'gram', label: 'Weight (kg)' },
                ]}
                onChange={(v) => set('stockUnit', v)}
              />
              {locked ? <span className="help">Open orders may hold this item. The server refuses a change until they are packed.</span> : null}
              {errors.stockUnit ? <ListError name="stockUnit" error={errors.stockUnit} /> : null}
            </div>
          </div>

          <div className="field">
            <span className="flabel">Sell by</span>
            <Segmented<'bands' | 'target'>
              label="Sell by"
              value={d.sizeMode}
              options={[
                { value: 'bands', label: 'Size bands' },
                { value: 'target', label: 'Target weight' },
              ]}
              onChange={(v) =>
                update((x) => ({
                  ...x,
                  sizeMode: v,
                  stockUnit: v === 'target' ? 'gram' : 'piece',
                  bands: v === 'bands' && !x.bands.length ? [{ key: newKey(), minG: '', maxG: '', isDefault: true }] : x.bands,
                }), v === 'bands' ? 'target' : 'bands')
              }
            />
            <span className="help">{d.sizeMode === 'bands' ? 'Whole fish or pieces in size ranges, like 500–600 g.' : 'Cut to the weight the customer asks for, like 250 g up to 2 kg.'}</span>
          </div>

          {d.sizeMode === 'bands' ? (
            <fieldset className="cat-fieldset">
              <legend className="t-label">Size bands</legend>
              <ul className="stack s8">
                {d.bands.map((b, i) => (
                  <li key={b.key} className="cat-band">
                    <div className="input-affix">
                      <input aria-label={`Band ${i + 1} smallest grams`} inputMode="numeric" placeholder="500" value={b.minG} onChange={(e) => update((x) => ({ ...x, bands: x.bands.map((y) => (y.key === b.key ? { ...y, minG: e.target.value } : y)) }), 'bands')} />
                      <span className="muted">g</span>
                    </div>
                    <span aria-hidden="true">to</span>
                    <div className="input-affix">
                      <input aria-label={`Band ${i + 1} largest grams`} inputMode="numeric" placeholder="600" value={b.maxG} onChange={(e) => update((x) => ({ ...x, bands: x.bands.map((y) => (y.key === b.key ? { ...y, maxG: e.target.value } : y)) }), 'bands')} />
                      <span className="muted">g</span>
                    </div>
                    <label className="check-label">
                      <input type="radio" className="check" name="default-band" checked={b.isDefault} onChange={() => update((x) => ({ ...x, bands: setDefaultBand(x.bands, b.key) }), 'bands')} />
                      <span className="t-caption">Default</span>
                    </label>
                    <span className="row cat-tools">
                      <button type="button" className="btn btn-quiet icon-btn" aria-label={`Remove band ${i + 1}`} onClick={() => update((x) => ({ ...x, bands: x.bands.filter((y) => y.key !== b.key) }), 'bands')}>
                        <Icon name="close" />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
              <ListError name="bands" error={errors.bands} />
              <button type="button" className="btn cat-add" disabled={d.bands.length >= 10} onClick={() => update((x) => ({ ...x, bands: [...x.bands, { key: newKey(), minG: '', maxG: '', isDefault: !x.bands.length }] }), 'bands')}>
                <Icon name="plus" />
                Add a size band
              </button>
            </fieldset>
          ) : (
            <fieldset className="cat-fieldset">
              <legend className="t-label">Target weight</legend>
              <div className="cat-grid3">
                {(
                  [
                    ['targetMinG', 'Smallest', '250'],
                    ['targetMaxG', 'Largest', '2000'],
                    ['targetStepG', 'Step', '250'],
                  ] as const
                ).map(([k, label, ph]) => (
                  <div key={k} className="field">
                    <label htmlFor={k === 'targetMinG' ? 'pf-target' : `pf-${k}`}>{label}</label>
                    <div className="input-affix" data-invalid={errors.target ? 'true' : undefined}>
                      <input
                        {...invalid(k === 'targetMinG' ? 'target' : k, errors.target)}
                        aria-describedby={errors.target ? 'pf-target-err' : undefined}
                        inputMode="numeric"
                        placeholder={ph}
                        value={d[k]}
                        onChange={(e) => update((x) => ({ ...x, [k]: e.target.value }), 'target')}
                      />
                      <span className="muted">g</span>
                    </div>
                  </div>
                ))}
              </div>
              {errors.target ? (
                <span id="pf-target-err" className="pill st-error err-pill cat-wrap">
                  {errors.target}
                </span>
              ) : null}
            </fieldset>
          )}
          <p className="t-caption muted">
            {est ? `Customers see about ${peso(est.centavos)} on the product card (${gramsLabel(est.grams)} at the price per kg).` : 'The card price is worked out from the default size and the price per kg.'}
          </p>
        </>
      ) : (
        <p className="t-caption muted">Sold by the piece or pack at the price above. Switch pricing to “By weight” for fish and cuts that are weighed at packing.</p>
      )}

      <fieldset className="cat-fieldset">
        <legend className="t-label">Prep options</legend>
        <p className="t-caption muted">Ways the shop can prepare it, like cleaned or filleted, with an optional fee.</p>
        <ul className="stack s8">
          {d.prepOptions.map((o, i) => (
            <li key={o.key} className="cat-prep">
              <input
                className="input"
                aria-label={`Prep option ${i + 1} name`}
                placeholder="Cleaned & gutted"
                maxLength={40}
                value={o.label}
                onChange={(e) => update((x) => ({ ...x, prepOptions: x.prepOptions.map((y) => (y.key === o.key ? { ...y, label: e.target.value } : y)) }), 'prepOptions')}
              />
              <div className="input-affix">
                <span aria-hidden="true">+₱</span>
                <input
                  aria-label={`Prep option ${i + 1} fee in pesos`}
                  inputMode="decimal"
                  placeholder="0"
                  value={o.fee}
                  onChange={(e) => update((x) => ({ ...x, prepOptions: x.prepOptions.map((y) => (y.key === o.key ? { ...y, fee: e.target.value } : y)) }), 'prepOptions')}
                />
              </div>
              <button type="button" className="btn btn-quiet icon-btn" aria-label={`Remove prep option ${o.label || i + 1}`} onClick={() => update((x) => ({ ...x, prepOptions: x.prepOptions.filter((y) => y.key !== o.key) }), 'prepOptions')}>
                <Icon name="close" />
              </button>
            </li>
          ))}
        </ul>
        <ListError name="prepOptions" error={errors.prepOptions} />
        <button type="button" className="btn cat-add" disabled={d.prepOptions.length >= 10} onClick={() => update((x) => ({ ...x, prepOptions: [...x.prepOptions, { key: newKey(), code: '', label: '', fee: '' }] }), 'prepOptions')}>
          <Icon name="plus" />
          Add a prep option
        </button>
      </fieldset>
    </section>
  );
}
